import { useState } from 'react';
import { WebSiteLabelSchema, WebSitePortSchema, type WebSite } from '@mcpanel/core';
import { api } from '../api/client';
import type { ServerView } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { Spinner } from '../components/feedback';
import { ErrorAlert } from '../components/ui';
import { usePoll } from '../hooks/usePoll';

/** VM の中の Web（ボイスチャットの Web 画面など）を https://<名前>.<ドメイン> で公開する */
export function WebSitesSection({ server }: { server: ServerView }) {
  const result = usePoll(() => api.webSites(server.id), 10000, [server.id]);
  const { me, notify } = useApp();
  const isOwner = me?.user?.role === 'owner';
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');
  const [port, setPort] = useState(8080);
  const data = result.data;
  const current: WebSite[] = data?.sites.map(({ label, port }) => ({ label, port })) ?? [];
  const labelError = label ? WebSiteLabelSchema.safeParse(label).error?.issues[0]?.message : undefined;
  const portError = WebSitePortSchema.safeParse(port).error?.issues[0]?.message;

  async function save(sites: WebSite[], done: string) {
    setBusy(true);
    try {
      await api.setWebSites(server.id, sites);
      result.reload();
      notify(done);
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card card-body stack">
      <h3 data-search-id="web-sites">Web の公開</h3>
      <p>
        サーバーの中で動く Web 画面（ボイスチャットの Web 版など）を、HTTPS
        のアドレスで外に出します。証明書は自動で取ります。
      </p>
      <ErrorAlert error={result.error} />
      {data && !data.sites.length && <p>公開している Web はありません。</p>}
      {data?.sites.map((site) => (
        <div key={site.label}>
          <p>
            <a href={site.url} target="_blank" rel="noreferrer">
              {site.url}
            </a>{' '}
            → 内部 {site.port}
          </p>
          {isOwner && (
            <button
              className="btn danger"
              disabled={busy}
              onClick={() => {
                if (!window.confirm(`${site.url} の公開をやめますか？`)) return;
                void save(
                  current.filter((s) => s.label !== site.label),
                  '公開をやめました',
                );
              }}
            >
              {busy && <Spinner />}
              公開をやめる
            </button>
          )}
        </div>
      ))}
      {data && isOwner && (
        <>
          <div className="field">
            <label htmlFor="web-site-label">公開する名前</label>
            <input
              id="web-site-label"
              className="input"
              maxLength={32}
              value={label}
              placeholder="voice"
              onChange={(e) => setLabel(e.target.value.trim().toLowerCase())}
              aria-invalid={Boolean(labelError)}
            />
            <span className="small muted">
              {label ? `https://${label}.${data.domain}` : `https://（名前）.${data.domain}`}
            </span>
            {labelError && (
              <div role="alert" style={{ color: 'var(--danger)' }}>
                {labelError}
              </div>
            )}
          </div>
          <div className="field">
            <label htmlFor="web-site-port">Web の内部ポート</label>
            <input
              id="web-site-port"
              className="input"
              type="number"
              min={1024}
              max={65535}
              value={port}
              onChange={(e) => setPort(Number(e.target.value))}
              aria-invalid={Boolean(portError)}
            />
            {portError && (
              <div role="alert" style={{ color: 'var(--danger)' }}>
                {portError}
              </div>
            )}
          </div>
          <p className="small muted">
            誰でも開けるアドレスになります。公開する Web には、そのアプリ側でパスワードなどを付けてください。
          </p>
          <button
            className="btn primary"
            disabled={busy || !label || Boolean(labelError) || Boolean(portError)}
            onClick={() =>
              void save(
                [...current.filter((s) => s.label !== label), { label, port }],
                `https://${label}.${data.domain} を公開しました`,
              ).then(() => setLabel(''))
            }
          >
            {busy && <Spinner />}
            公開する
          </button>
        </>
      )}
      {data && !isOwner && <p className="small muted">公開の変更はオーナーだけができます。</p>}
    </section>
  );
}
